import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { Plus, Trash2, Edit3, Copy, Workflow } from "lucide-react";

const PORTALS = ["ORGANIZATION", "DISTRIBUTOR", "VENDOR", "DEALER", "ADMIN"];
const PERMS = ["view", "create", "edit", "delete", "approve", "export", "import", "reports", "settings", "api", "workflow", "marketplace", "inventory", "crm", "billing", "production", "warehouse"];

const BLANK = {
  code: "", name: "", parent_code: "", portal: "ORGANIZATION", icon: "Users",
  max_children: 0, base_cost_per_user: 0,
  min_in_basic: 0, max_in_basic: 0, min_in_premium: 0, max_in_premium: 0, min_in_enterprise: 0, max_in_enterprise: 0,
  permissions: ["view"], landing_visible: true, active: true, sort_order: 99,
};

export default function PlatformRoleEngine() {
  const [roles, setRoles] = useState([]);
  const [filterPortal, setFilterPortal] = useState("ALL");
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const [form, setForm] = useState(BLANK);

  const load = async () => { const { data } = await api.get("/platform/roles"); setRoles(data); };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => filterPortal === "ALL" ? roles : roles.filter((r) => r.portal === filterPortal), [roles, filterPortal]);
  const parentChoices = useMemo(() => roles.filter((r) => r.portal === form.portal && r.code !== form.code), [roles, form.portal, form.code]);

  const startEdit = (r) => { setEdit(r); setForm({ ...r, parent_code: r.parent_code || "" }); setOpen(true); };
  const startNew = () => { setEdit(null); setForm(BLANK); setOpen(true); };

  const save = async () => {
    try {
      const payload = { ...form, parent_code: form.parent_code || null };
      if (edit) await api.patch(`/platform/roles/${edit.id}`, payload);
      else await api.post("/platform/roles", payload);
      toast.success(edit ? "Role updated" : "Role created");
      setOpen(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const clone = async (r) => {
    try { await api.post(`/platform/roles/${r.id}/clone`); toast.success("Cloned"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const remove = async (r) => {
    if (!window.confirm(`Delete role ${r.name}?`)) return;
    try { await api.delete(`/platform/roles/${r.id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const togglePerm = (p) => setForm((f) => ({
    ...f, permissions: f.permissions.includes(p) ? f.permissions.filter((x) => x !== p) : [...f.permissions, p]
  }));

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-4 sm:space-y-6" data-testid="admin-platform-roles">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-black flex items-center gap-2"><Workflow className="w-7 h-7 text-primary" /> Role Engine</h1>
          <p className="text-muted-foreground">Create / nest / price every role across every portal. Subscribers see this live.</p>
        </div>
        <div className="flex items-end gap-2">
          <Select value={filterPortal} onValueChange={setFilterPortal}>
            <SelectTrigger className="w-48" data-testid="role-filter-portal"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL">All portals</SelectItem>
              {PORTALS.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button onClick={startNew} data-testid="role-new"><Plus className="w-4 h-4 mr-1" /> New role</Button>
        </div>
      </div>

      <div className="surface-card p-4 overflow-x-auto">
        <Table>
          <TableHeader><TableRow>
            <TableHead>Code</TableHead><TableHead>Name</TableHead><TableHead>Portal</TableHead>
            <TableHead>Parent</TableHead><TableHead className="text-right">Cost/user</TableHead>
            <TableHead>Premium range</TableHead><TableHead>Visible</TableHead><TableHead className="text-right">Actions</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {filtered.map((r) => (
              <TableRow key={r.id} data-testid={`role-row-${r.code}`}>
                <TableCell className="font-mono text-xs">{r.code}</TableCell>
                <TableCell className="font-medium">{r.name}</TableCell>
                <TableCell><Badge variant="outline">{r.portal}</Badge></TableCell>
                <TableCell className="text-xs font-mono text-muted-foreground">{r.parent_code || "—"}</TableCell>
                <TableCell className="text-right font-mono">₹{r.base_cost_per_user}</TableCell>
                <TableCell className="text-xs">{r.min_in_premium}–{r.max_in_premium}</TableCell>
                <TableCell>{r.landing_visible ? <Badge>YES</Badge> : <Badge variant="outline">NO</Badge>}</TableCell>
                <TableCell className="text-right space-x-1">
                  <Button size="sm" variant="outline" onClick={() => startEdit(r)}><Edit3 className="w-3 h-3" /></Button>
                  <Button size="sm" variant="outline" onClick={() => clone(r)}><Copy className="w-3 h-3" /></Button>
                  <Button size="sm" variant="outline" onClick={() => remove(r)}><Trash2 className="w-3 h-3 text-red-500" /></Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{edit ? "Edit role" : "New role"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Code</Label><Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} disabled={!!edit} data-testid="role-form-code" /></div>
            <div><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="role-form-name" /></div>
            <div>
              <Label>Portal</Label>
              <Select value={form.portal} onValueChange={(v) => setForm({ ...form, portal: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{PORTALS.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Parent role (optional)</Label>
              <Select value={form.parent_code || "__NONE__"} onValueChange={(v) => setForm({ ...form, parent_code: v === "__NONE__" ? "" : v })}>
                <SelectTrigger><SelectValue placeholder="None (root)" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__NONE__">None (root)</SelectItem>
                  {parentChoices.map((p) => <SelectItem key={p.code} value={p.code}>{p.name} ({p.code})</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div><Label>Icon (lucide name)</Label><Input value={form.icon} onChange={(e) => setForm({ ...form, icon: e.target.value })} /></div>
            <div><Label>Max children</Label><Input type="number" value={form.max_children} onChange={(e) => setForm({ ...form, max_children: parseInt(e.target.value || "0") })} /></div>
            <div><Label>Cost / user / mo (₹)</Label><Input type="number" value={form.base_cost_per_user} onChange={(e) => setForm({ ...form, base_cost_per_user: parseFloat(e.target.value || "0") })} /></div>
            <div><Label>Sort order</Label><Input type="number" value={form.sort_order} onChange={(e) => setForm({ ...form, sort_order: parseInt(e.target.value || "0") })} /></div>
            <div className="col-span-2 grid grid-cols-6 gap-2 p-3 rounded-lg border">
              <Label className="col-span-2 text-xs uppercase tracking-wide text-muted-foreground">Plan limits</Label>
              <div></div><div></div><div></div><div></div>
              {["basic", "premium", "enterprise"].map((plan) => (
                <div key={plan} className="col-span-6 grid grid-cols-6 gap-2 items-center">
                  <Label className="col-span-2 text-sm capitalize">{plan}</Label>
                  <div className="col-span-2"><Label className="text-[10px]">Min</Label><Input type="number" value={form[`min_in_${plan}`]} onChange={(e) => setForm({ ...form, [`min_in_${plan}`]: parseInt(e.target.value || "0") })} /></div>
                  <div className="col-span-2"><Label className="text-[10px]">Max</Label><Input type="number" value={form[`max_in_${plan}`]} onChange={(e) => setForm({ ...form, [`max_in_${plan}`]: parseInt(e.target.value || "0") })} /></div>
                </div>
              ))}
            </div>
            <div className="col-span-2">
              <Label>Permissions</Label>
              <div className="flex flex-wrap gap-2 mt-2">
                {PERMS.map((p) => (
                  <button key={p} type="button" onClick={() => togglePerm(p)}
                    className={`px-2.5 py-1 rounded-full text-xs font-medium border ${form.permissions.includes(p) ? "bg-primary text-primary-foreground border-primary" : "bg-transparent border-border text-muted-foreground"}`}
                    data-testid={`role-perm-${p}`}
                  >{p}</button>
                ))}
              </div>
            </div>
            <div className="flex items-center gap-2"><Switch checked={form.landing_visible} onCheckedChange={(v) => setForm({ ...form, landing_visible: v })} /><Label>Show on landing</Label></div>
            <div className="flex items-center gap-2"><Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} /><Label>Active</Label></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} data-testid="role-save">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
